<script setup lang="ts">
    import type { AiMessage, FreeLineData, CreateContactData, CreateWorksiteData, VerificationState } from '~/stores/aiAssistantTypes';
    import { renderMarkdown } from '~/utils/renderMarkdown';

    const props = defineProps<{
        messages: AiMessage[];
        currentStreamingMessage: string;
        statusLabel: string | null;
        isProcessing: boolean;
        verifications: Map<string, VerificationState>;
        createdDocumentType: string | null;
    }>();

    const emit = defineEmits<{
        'option-selected': [messageId: string, value: string, metadata?: Record<string, any>];
        'suggestion-clicked': [text: string];
        'open-inline-form': [parentMessageId: string, value: string];
        'submit-form': [parentMessageId: string, formMessageId: string, payload: { freeLineData?: FreeLineData; createContactData?: CreateContactData; createWorksiteData?: CreateWorksiteData; selectedExistingUuid?: string }];
        'cancel-form': [formMessageId: string];
        'dismiss-verification': [documentId: string];
        'toggle-minimize-verification': [documentId: string];
    }>();

    const expandedVerifications = computed(() => {
        const entries: [string, VerificationState][] = [];
        for (const [docId, v] of props.verifications) {
            if (!v.minimized) entries.push([docId, v]);
        }
        return entries;
    });

    const messagesContainer = ref<HTMLElement | null>(null);

    // Markdown rendering driven by Vue reactivity (computed) instead of requestAnimationFrame.
    // Browsers throttle/pause rAF for backgrounded or occluded tabs, which previously left
    // streaming text invisible until the window regained focus.
    const renderedStreamingMessage = computed(() =>
        props.currentStreamingMessage ? renderMarkdown(props.currentStreamingMessage) : '',
    );

    const scrollToBottom = () => {
        nextTick(() => {
            if (messagesContainer.value) {
                messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight;
            }
        });
    };

    const scrollToBottomDelayed = () => {
        nextTick(() => setTimeout(scrollToBottom, 50));
    };

    let scrollRafId: number | null = null;
    const throttledScrollToBottom = () => {
        if (scrollRafId) return;
        scrollRafId = requestAnimationFrame(() => {
            scrollToBottom();
            scrollRafId = null;
        });
    };

    onMounted(() => {
        if (props.messages.length > 0) {
            scrollToBottomDelayed();
        }
    });

    onBeforeUnmount(() => {
        if (scrollRafId) cancelAnimationFrame(scrollRafId);
    });

    watch(() => props.messages.length, scrollToBottomDelayed);
    watch(() => props.currentStreamingMessage, throttledScrollToBottom);

    // The verification card animates one step at a time below the chat fold. Without this
    // watcher the user never sees the card unless they manually scroll.
    const verificationFootprint = computed(() => {
        let total = 0;
        for (const [, v] of props.verifications) {
            total += v.steps.length + (v.isComplete ? 1 : 0);
        }
        return `${props.verifications.size}:${total}`;
    });
    watch(verificationFootprint, throttledScrollToBottom);
</script>

<template>
    <div
        ref="messagesContainer"
        class="flex-1 min-h-0 overflow-y-auto nice-scrollbar py-6"
    >
        <div class="max-w-[720px] w-full mx-auto px-6 space-y-[10px]">
            <template v-if="messages.length === 0 && !currentStreamingMessage">
                <AiPageGreetingEmptyState @suggestion-clicked="(text) => emit('suggestion-clicked', text)" />
            </template>

            <AiAssistantMessageBubble
                v-for="msg in messages"
                :key="msg.id"
                :message="msg"
                @option-selected="(value, metadata) => emit('option-selected', msg.id, value, metadata)"
                @open-inline-form="(parentId, value) => emit('open-inline-form', parentId, value)"
                @submit-form="(parentId, formId, payload) => emit('submit-form', parentId, formId, payload)"
                @cancel-form="(formId) => emit('cancel-form', formId)"
            />

            <AiVerificationCard
                v-for="[docId, verification] in expandedVerifications"
                :key="`verify-${docId}`"
                :document-id="docId"
                :document-type="createdDocumentType ?? 'quote'"
                :steps="verification.steps"
                :is-complete="verification.isComplete"
                :final-status="verification.finalStatus"
                :auto-fixing="verification.autoFixing ?? false"
                @dismiss="(id) => emit('dismiss-verification', id)"
                @toggle-minimize="(id) => emit('toggle-minimize-verification', id)"
            />

            <!-- Streaming message (in progress) — yellow dashed narration pill, matches the
                 design's ToolNote/loader treatment. On finalize the formatter retro-classifies
                 as a real bubble or keeps as pill (when interactive_choices arrived). -->
            <div
                v-if="currentStreamingMessage"
                class="flex items-center py-1"
            >
                <div
                    class="inline-flex items-start gap-2 px-[14px] py-[8px] rounded-md font-inter text-[12px] text-[#42484E]"
                    style="background: rgba(255, 196, 0, 0.08); border: 1px dashed rgba(255, 196, 0, 0.5);"
                >
                    <i class="fa-solid fa-wand-magic-sparkles text-[11px] text-[#E8B102] mt-[3px]" />
                    <div
                        class="ai-markdown leading-[18px] overflow-x-auto"
                        v-html="renderedStreamingMessage"
                    />
                </div>
            </div>

            <!-- Status indicator -->
            <div
                v-if="isProcessing && statusLabel"
                class="flex items-center gap-[8px] py-[5px]"
            >
                <i class="fa-solid fa-wand-magic-sparkles text-[11px] text-[#E8B102]" />
                <span class="font-inter text-[12px] text-gray-500 italic">{{ statusLabel }}</span>
            </div>
        </div>
    </div>
</template>
